import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PhotoIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';

import { useResolvedImageSrc } from '@/components/proof/useResolvedImageSrc';
import type { RiskImage } from '@/sections/poe/utils/collectRiskImages';

// EvidenceImageGallery aggregates every image for a risk (agent screenshots,
// uploaded evidence images, processor screenshots) into a captioned thumbnail
// grid. Clicking a thumbnail opens a click-to-zoom lightbox with keyboard
// navigation (←/→/Escape) and an {i}/{n} counter. Each source is resolved via
// useResolvedImageSrc, so storage keys, blob URLs, and data URIs all render.
export function EvidenceImageGallery({ images }: { images: RiskImage[] }) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  if (images.length === 0) return null;

  return (
    <div className="p-4">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {images.map((image, index) => (
          <GalleryThumbnail
            key={`${image.registryKey ?? image.src}-${index}`}
            image={image}
            onOpen={() => setLightboxIndex(index)}
          />
        ))}
      </div>
      {lightboxIndex !== null ? (
        <GalleryLightbox
          images={images}
          index={lightboxIndex}
          onIndexChange={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      ) : null}
    </div>
  );
}

function GalleryThumbnail({
  image,
  onOpen,
}: {
  image: RiskImage;
  onOpen: () => void;
}) {
  const { src, isError } = useResolvedImageSrc(image.src);

  return (
    <figure className="flex flex-col gap-2">
      <button
        type="button"
        onClick={onOpen}
        aria-label={
          image.caption ? `View image: ${image.caption}` : 'View image'
        }
        className="border-chariot-stroke focus-visible:ring-chariot-brand-primary block overflow-hidden rounded-md border focus:outline-none focus-visible:ring-2"
      >
        {isError ? (
          <div className="bg-chariot-background-secondary text-chariot-text-secondary flex aspect-video w-full flex-col items-center justify-center gap-1">
            <PhotoIcon className="size-6" />
            <span className="text-xs">Failed to load</span>
          </div>
        ) : src ? (
          <img
            src={src}
            alt={image.alt ?? image.caption ?? ''}
            className="aspect-video w-full object-cover"
          />
        ) : (
          <div className="bg-chariot-background-secondary aspect-video w-full animate-pulse" />
        )}
      </button>
      {image.caption ? (
        <figcaption className="text-chariot-text-secondary text-xs">
          {image.caption}
        </figcaption>
      ) : null}
    </figure>
  );
}

function GalleryLightbox({
  images,
  index,
  onIndexChange,
  onClose,
}: {
  images: RiskImage[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}) {
  const image = images[index];
  const { src, isLoading, isError } = useResolvedImageSrc(image?.src);
  const hasPrev = index > 0;
  const hasNext = index < images.length - 1;
  const overlayRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  // Move focus into the lightbox on open and restore it to the invoking
  // thumbnail on close. Runs once per mount — the lightbox unmounts when closed,
  // so navigation between images does not re-steal focus.
  useEffect(() => {
    returnFocusRef.current = document.activeElement as HTMLElement | null;
    overlayRef.current?.focus();
    return () => returnFocusRef.current?.focus?.();
  }, []);

  // Escape / arrows are handled on a CAPTURE-phase document listener so a single
  // Escape closes ONLY the lightbox. The host risk drawer arms Escape via
  // useDrawerEscape (react-hotkeys-hook, bound on `document` in the BUBBLE
  // phase); intercepting in the capture phase and calling stopPropagation()
  // means that handler never fires, so the drawer stays open. Handling the
  // arrows here too keeps them from leaking to the table's arrow-nav while the
  // lightbox is open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        if (index > 0) onIndexChange(index - 1);
      } else if (e.key === 'ArrowRight') {
        if (index < images.length - 1) onIndexChange(index + 1);
      } else if (e.key === 'Escape') {
        onClose();
      } else {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [index, images.length, onIndexChange, onClose]);

  const arrowClass =
    'absolute top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-2 text-white/80 transition-colors hover:bg-black/60 hover:text-white';

  return (
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-label={
        image?.caption ? `Image viewer: ${image.caption}` : 'Image viewer'
      }
      tabIndex={-1}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-12 focus:outline-none"
      onClick={onClose}
    >
      <div className="absolute top-4 right-4 flex items-center gap-3">
        <span className="text-sm text-white/70">
          {index + 1} / {images.length}
        </span>
        <button
          onClick={e => {
            e.stopPropagation();
            onClose();
          }}
          className="text-white/70 transition-colors hover:text-white"
          aria-label="Close"
        >
          <XMarkIcon className="size-6" />
        </button>
      </div>

      {hasPrev ? (
        <button
          onClick={e => {
            e.stopPropagation();
            onIndexChange(index - 1);
          }}
          className={`${arrowClass} left-4`}
          aria-label="Previous image"
        >
          <ChevronLeftIcon className="size-8" />
        </button>
      ) : null}

      <figure
        className="flex max-h-full max-w-full flex-col items-center gap-3"
        onClick={e => e.stopPropagation()}
      >
        {isLoading ? (
          <div className="aspect-video w-80 max-w-full animate-pulse rounded-md bg-white/10" />
        ) : isError ? (
          <div className="text-sm text-white/80">Failed to load image</div>
        ) : src ? (
          <img
            src={src}
            alt={image?.alt ?? image?.caption ?? ''}
            className="max-h-full max-w-full rounded-md object-contain"
          />
        ) : null}
        {image?.caption ? (
          <figcaption className="text-sm text-white/80">
            {image.caption}
          </figcaption>
        ) : null}
      </figure>

      {hasNext ? (
        <button
          onClick={e => {
            e.stopPropagation();
            onIndexChange(index + 1);
          }}
          className={`${arrowClass} right-4`}
          aria-label="Next image"
        >
          <ChevronRightIcon className="size-8" />
        </button>
      ) : null}
    </div>
  );
}
